// src/ui/TagPicker.jsx — "Tag people" in the Vibes composer (and in Edit post).
//
// The phone's twin is src/components/TagPeopleSheet.tsx. Friends first, then —
// as soon as letters are typed — anybody the server's people search finds
// (searchRoutes.js leaves out guests, deleted accounts and both sides of a block).
// Whether a person may actually be tagged (their "who can tag me", review,
// blocks) is the SERVER's answer after posting; this never guesses at another
// person's privacy settings.
// 🟢 Facebook's list: "Suggestions", and under each name who they are to you —
//    "Friend · 22 mutual friends" / "Profile · 3 mutual friends" (counted by the
//    server, searchRoutes.js mutual) — with a square checkbox.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, documentId, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { serverSearch, mutualFriends } from '../services/vibeSearch';
import { composeL, personSubline } from '../services/postCompose';
import { TAG_MAX } from '../services/postTags';
import { fold } from '../services/searchRank';
import Pic from './VibeAvatar';
import { X, Check } from './icons';

const FRIENDS_MAX = 120;
const cache = new Map();

async function loadFriends(ids) {
  const want = ids.slice(0, FRIENDS_MAX);
  const missing = want.filter((id) => !cache.has(id));
  for (let i = 0; i < missing.length; i += 10) {
    const chunk = missing.slice(i, i + 10);
    try {
      // eslint-disable-next-line no-await-in-loop
      const s = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)));
      s.docs.forEach((d) => {
        const u = d.data() || {};
        if (u.isGuest === true || u.deleted === true) return;
        cache.set(d.id, { uid: d.id, name: String(u.fullName || u.username || ''), image: u.profileImage || null });
      });
    } catch { /* a chunk that fails is a few friends not offered — search still finds them */ }
  }
  return want.map((id) => cache.get(id)).filter((p) => p && p.name).sort((a, b) => a.name.localeCompare(b.name));
}

export default function TagPicker({ value, friendIds, myUid, onDone, onClose }) {
  const [picked, setPicked] = useState(value || []);
  const [q, setQ] = useState('');
  const [friends, setFriends] = useState([]);
  const [found, setFound] = useState([]);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({});
  const seq = useRef(0);
  const asked = useRef(new Set());
  const lang = (typeof navigator !== 'undefined' && navigator.language) || 'en';
  const friendSet = useMemo(() => new Set(Array.from(friendIds || []).map(String)), [friendIds]);

  useEffect(() => {
    const ids = Array.from(friendIds || []).map(String).filter((x) => x && x !== myUid);
    let live = true;
    loadFriends(ids).then((r) => { if (live) setFriends(r); });
    return () => { live = false; };
  }, [friendIds, myUid]);

  useEffect(() => {
    const text = q.trim();
    if (!text) { setFound([]); setBusy(false); return undefined; }
    const mine = ++seq.current;
    setBusy(true);
    const tm = setTimeout(async () => {
      const r = await serverSearch(text, 'people');
      if (mine !== seq.current) return;
      setFound(((r && r.people) || []).filter((p) => !p.pageId && p.uid && p.uid !== myUid)
        .map((p) => ({ uid: String(p.uid), name: String(p.name || ''), image: p.image || null })));
      setBusy(false);
    }, 280);
    return () => clearTimeout(tm);
  }, [q, myUid]);

  const rows = useMemo(() => {
    const f = fold(q);
    const fr = f ? friends.filter((p) => fold(p.name).includes(f)) : friends;
    const seen = new Set(fr.map((p) => p.uid));
    return fr.concat(found.filter((p) => !seen.has(p.uid)));
  }, [friends, found, q]);

  /* the second line under each name — asked once per person, for the rows shown */
  useEffect(() => {
    const need = rows.slice(0, 60).map((p) => p.uid).filter((u) => !asked.current.has(u));
    if (!need.length) return undefined;
    need.forEach((u) => asked.current.add(u));
    let live = true;
    mutualFriends(need).then((m) => { if (live && m) setMeta((cur) => ({ ...cur, ...m })); });
    return () => { live = false; };
  }, [rows]);

  const on = (uid) => picked.some((p) => p.uid === uid);
  const toggle = (p) => setPicked((cur) => (cur.some((x) => x.uid === p.uid)
    ? cur.filter((x) => x.uid !== p.uid)
    : (cur.length >= TAG_MAX ? cur : cur.concat([p]))));

  return (
    <div className="vb-fixed-share" onClick={onClose}>
      <div className="vb-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Tag people">
        <div className="vb-sheet-head">
          <b>Tag people</b>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><X width={18} height={18} /></button>
        </div>
        <div className="vb-sheet-search">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={composeL('search', lang)} />
        </div>
        {picked.length > 0 && (
          <div className="vb-tagchips">
            {picked.map((p) => (
              <button key={p.uid} className="vb-tagchip on" onClick={() => toggle(p)} title="Remove">
                {p.name} <X width={12} height={12} />
              </button>
            ))}
            {picked.length >= TAG_MAX && <span className="vb-sub">You can tag up to {TAG_MAX} people</span>}
          </div>
        )}
        <div className="vb-sheet-body">
          {!q.trim() && friends.length > 0 && <div className="vb-tag-suggest">{composeL('suggestions', lang)}</div>}
          {busy && !rows.length && <div className="vb-sub" style={{ padding: 12 }}>Searching…</div>}
          {!busy && !rows.length && (
            <div className="vb-sub" style={{ padding: 12 }}>{q.trim() ? 'No one found' : 'Type a name to find people to tag'}</div>
          )}
          {rows.map((p) => (
            <button key={p.uid} className="vb-row wide" onClick={() => toggle(p)} aria-pressed={on(p.uid)}>
              <Pic src={p.image} name={p.name} size={40} />
              <div className="vb-tag-who">
                <b>{p.name}</b>
                <span>{personSubline({ friend: meta[p.uid] ? meta[p.uid].friend : friendSet.has(p.uid), mutual: meta[p.uid] ? meta[p.uid].mutual : 0 }, lang)}</span>
              </div>
              <span className={`vb-tagcheck sq ${on(p.uid) ? 'on' : ''}`}>{on(p.uid) && <Check width={14} height={14} />}</span>
            </button>
          ))}
        </div>
        <div className="vb-sheet-foot">
          <button className="lg-go" onClick={() => onDone(picked)}>Done{picked.length ? ` · ${picked.length}` : ''}</button>
        </div>
      </div>
    </div>
  );
}
